// ─────────────────────────────────────────────────────────────────────────────
// nkParseStrokeFrame.js — NK EmPower Oarlock dataStreamB parser
//
// Reverse-engineered from HCI snoop + nRF52840 sniffer captures.
// Notification char: 0x12630003-… (dataStreamB)  · 19 bytes per stroke.
//
// PAYLOAD FORMAT
//   off  size  type    scale     field
//   ───  ────  ──────  ────────  ─────────────────────────────────
//    0    2    u16LE   1 ms      Stroke timestamp (wraps ~65 s)
//    2    2    u16LE   1 ms      Stroke period — SPM = 60000 / val
//    4    2    u16LE   1 ms      Drive time
//    6    2    u16LE   0.1 J     Work per stroke
//    8    2    u16LE   0.1 mm    Drive length
//   10    2    u16LE   0.01 N    Peak force
//   12    2    u16LE   0.1 N     Average force
//   14    2    u16LE   0.01 W    Power
//   16    1    u8      1°        Catch angle magnitude (0x80 = null)
//   17    1    u8      1°        Finish angle           (0x80 = null)
//   18    1    u8      1°        Effective length       (0x80 = null)
//
// SENTINELS
//   u16 0x0000 / 0xFFFF → null
//   u8  0x80           → null
//
// Catch angle stored as positive magnitude; NK convention is negative at
// catch, so we negate it for downstream consumers.
// ─────────────────────────────────────────────────────────────────────────────

const NULL_U8 = 0x80;
const isNullU16 = (v) => v === 0x0000 || v === 0xFFFF;

/**
 * Parse a 19-byte EmPower dataStreamB notification.
 *
 * @param {DataView|Uint8Array|ArrayBuffer} data
 * @returns {object|null}  Decoded stroke or null on bad input
 */
export function parseStrokeFrame(data) {
  if (!data) return null;
  let view;
  if (data instanceof DataView) {
    view = data;
  } else if (data instanceof Uint8Array) {
    view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  } else if (data instanceof ArrayBuffer) {
    view = new DataView(data);
  } else {
    return null;
  }
  if (view.byteLength < 19) return null;

  const rawTimestamp = view.getUint16(0, true);
  const rawPeriod    = view.getUint16(2, true);
  const rawDriveMs   = view.getUint16(4, true);
  const rawWork      = view.getUint16(6, true);
  const rawDriveLen  = view.getUint16(8, true);
  const rawPeakForce = view.getUint16(10, true);
  const rawAvgForce  = view.getUint16(12, true);
  const rawPower     = view.getUint16(14, true);
  const rawCatchAng  = view.getUint8(16);
  const rawFinishAng = view.getUint8(17);
  const rawEffLen    = view.getUint8(18);

  const isValid =
    !isNullU16(rawPeriod) && rawPeriod > 0 &&
    !isNullU16(rawPower) &&
    rawCatchAng !== NULL_U8;

  const strokePeriodMs = rawPeriod;
  const spm = strokePeriodMs > 0
    ? Math.round((60000 / strokePeriodMs) * 10) / 10
    : 0;
  const driveTimeMs    = isNullU16(rawDriveMs)   ? 0 : rawDriveMs;
  const recoveryTimeMs = strokePeriodMs - driveTimeMs;
  const workPerStrokeJ = isNullU16(rawWork)      ? 0 : Math.round(rawWork / 10 * 10) / 10;
  const driveLengthCm  = isNullU16(rawDriveLen)  ? 0 : Math.round(rawDriveLen / 10 * 10) / 10;
  const peakForceN     = isNullU16(rawPeakForce) ? 0 : Math.round(rawPeakForce / 100 * 10) / 10;
  const avgForceN      = isNullU16(rawAvgForce)  ? 0 : Math.round(rawAvgForce / 10 * 10) / 10;
  const powerW         = isNullU16(rawPower)     ? 0 : Math.round(rawPower / 100 * 10) / 10;
  const catchAngleDeg  = rawCatchAng === NULL_U8  ? 0   : -rawCatchAng;
  const finishAngleDeg = rawFinishAng === NULL_U8 ? null : rawFinishAng;
  const effectiveLengthDeg = rawEffLen === NULL_U8 ? null : rawEffLen;

  return {
    timestampMs: rawTimestamp,
    strokePeriodMs,
    spm,
    driveTimeMs,
    recoveryTimeMs,
    peakForceN,
    avgForceN,
    workPerStrokeJ,
    powerW,
    driveLengthCm,
    catchAngleDeg,
    finishAngleDeg,
    effectiveLengthDeg,
    isValid,
  };
}
